import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, Palette } from '@core/config/constants';
import { TextButton } from '@ui/components/TextButton';
import { RhythmConfig } from '@modules/rhythm/config';

/**
 * Minijuego de ritmo: aparecen estrellas que se encogen; el jugador debe tocarlas
 * antes de que desaparezcan. Sirve como referencia de como se estructura un modulo:
 * hereda de BaseScene, usa el perfil compartido y guarda su propio puntaje.
 */
export class RhythmScene extends BaseScene {
  private score = 0;
  private combo = 0;
  private remainingMs: number = RhythmConfig.roundDurationMs;
  private isPlaying = false;

  private scoreText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private spawnTimer?: Phaser.Time.TimerEvent;
  private countdownTimer?: Phaser.Time.TimerEvent;

  public constructor() {
    super(RhythmConfig.key);
  }

  public create(): void {
    this.resetState();
    this.add.rectangle(this.centerX, this.centerY, this.scale.width, this.scale.height, Palette.backgroundAlt);

    this.scoreText = this.add.text(40, 30, '', {
      fontFamily: 'Trebuchet MS, sans-serif',
      fontSize: '34px',
      color: Palette.textLight,
    });

    this.timerText = this.add
      .text(this.scale.width - 40, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.refreshHud();
    this.startRound();
  }

  private resetState(): void {
    this.score = 0;
    this.combo = 0;
    this.remainingMs = RhythmConfig.roundDurationMs;
    this.isPlaying = false;
  }

  private startRound(): void {
    this.isPlaying = true;

    this.spawnTimer = this.time.addEvent({
      delay: RhythmConfig.spawnIntervalMs,
      loop: true,
      callback: () => this.spawnNote(),
    });

    this.countdownTimer = this.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => this.tickCountdown(100),
    });
  }

  private tickCountdown(deltaMs: number): void {
    this.remainingMs = Math.max(0, this.remainingMs - deltaMs);
    this.refreshHud();
    if (this.remainingMs <= 0) {
      this.endRound();
    }
  }

  private spawnNote(): void {
    if (!this.isPlaying) {
      return;
    }

    const margin = RhythmConfig.noteRadius + 20;
    const x = Phaser.Math.Between(margin, this.scale.width - margin);
    const y = Phaser.Math.Between(margin + 80, this.scale.height - margin);

    const note = this.add.circle(x, y, RhythmConfig.noteRadius, Palette.highlight);
    note.setInteractive({ useHandCursor: true });

    const star = this.add.text(x, y, '⭐', { fontSize: '52px' }).setOrigin(0.5);

    const destroyNote = (): void => {
      note.destroy();
      star.destroy();
    };

    note.once(Phaser.Input.Events.POINTER_DOWN, () => {
      this.registerHit();
      this.tweens.add({
        targets: [note, star],
        scale: 1.6,
        alpha: 0,
        duration: 150,
        onComplete: destroyNote,
      });
    });

    this.tweens.add({
      targets: [note, star],
      scale: 0.3,
      duration: RhythmConfig.noteLifetimeMs,
      onComplete: () => {
        if (note.active) {
          this.registerMiss();
          destroyNote();
        }
      },
    });
  }

  private registerHit(): void {
    this.combo += 1;
    this.score += RhythmConfig.pointsPerHit + this.combo * RhythmConfig.comboBonus;
    this.audio.playHit();
    this.refreshHud();
  }

  private registerMiss(): void {
    this.combo = 0;
    this.audio.playMiss();
    this.refreshHud();
  }

  private refreshHud(): void {
    const seconds = Math.ceil(this.remainingMs / 1000);
    this.scoreText.setText(`Puntos: ${this.score}   Combo: x${this.combo}`);
    this.timerText.setText(`⏱ ${seconds}s`);
  }

  private endRound(): void {
    this.isPlaying = false;
    this.spawnTimer?.remove();
    this.countdownTimer?.remove();

    const isRecord = this.profile.submitScore(RhythmConfig.key, this.score);
    const coins = Math.floor(this.score / 100) * RhythmConfig.coinsPer100Points;
    this.profile.addCoins(coins);

    this.audio.playReward();
    this.showResults(isRecord, coins);
  }

  private showResults(isRecord: boolean, coins: number): void {
    const overlay = this.add.rectangle(
      this.centerX,
      this.centerY,
      this.scale.width,
      this.scale.height,
      Palette.background,
      0.85,
    );
    overlay.setInteractive();

    this.add
      .text(this.centerX, this.centerY - 150, isRecord ? '¡Nuevo record!' : '¡Bien hecho!', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '52px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(
        this.centerX,
        this.centerY - 60,
        `Puntos: ${this.score}\nEstrellas ganadas: ${coins}`,
        {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '32px',
          color: Palette.textLight,
          align: 'center',
        },
      )
      .setOrigin(0.5);

    new TextButton(this, {
      x: this.centerX - 180,
      y: this.centerY + 80,
      label: 'Jugar de nuevo',
      color: Palette.secondary,
      onClick: () => this.scene.restart(),
    });

    new TextButton(this, {
      x: this.centerX + 180,
      y: this.centerY + 80,
      label: 'Volver al menu',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }
}
